import { Router } from '@angular/router';
import { PerfilSeccionId } from '../../data/perfil-microempresario.content';

/** Rutas absolutas de secciones del perfil (null = aún no disponible). */
export const PERFIL_SECTION_ROUTES: Record<PerfilSeccionId, string | null> = {
  'sobre-ti': '/microempresario/perfil/sobre-ti',
  'sobre-negocio': '/microempresario/perfil/sobre-negocio',
  'sobre-ingresos': '/microempresario/perfil/sobre-ingresos',
};

export const PERFIL_HUB_ROUTE = '/microempresario/perfil';
export const PERFIL_ALIANZA_ROUTE = '/microempresario/perfil/alianza';

/** Query param para abrir un subpaso del set de pasos (`0`, `1`, …). */
export const PERFIL_PASO_QUERY = 'paso';

export function parsePerfilPasoQuery(raw: string | null | undefined): number | null {
  if (raw == null || raw === '') {
    return null;
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) {
    return null;
  }
  return Math.floor(n);
}
export const PERFIL_SOLICITUD_ENVIADA_ROUTE =
  '/microempresario/perfil/solicitud-enviada';

/** Orden del flujo: siguiente sección tras completar la actual. */
export const PERFIL_NEXT_SECTION: Record<
  PerfilSeccionId,
  PerfilSeccionId | null
> = {
  'sobre-ti': 'sobre-negocio',
  'sobre-negocio': 'sobre-ingresos',
  'sobre-ingresos': null,
};

/** PJ: empresa (sobre-ti) → ingresos (sin sobre-negocio). */
export const PERFIL_NEXT_SECTION_PJ: Record<
  PerfilSeccionId,
  PerfilSeccionId | null
> = {
  'sobre-ti': 'sobre-ingresos',
  'sobre-negocio': 'sobre-ingresos',
  'sobre-ingresos': null,
};

/** State de Router: abrir modal intro al llegar a sección/hub en 0%. */
export const PERFIL_NAV_SHOW_INTRO = 'showIntro';
/** State de Router: id de sección del intro (p. ej. en hub sin ruta aún). */
export const PERFIL_NAV_INTRO_SECCION = 'introSeccionId';
/** State de Router: abrir modal enviar al llegar al hub (perfil 100 %). */
export const PERFIL_NAV_SHOW_ENVIAR = 'showEnviarModal';
/** State de Router: intentar landing alianza al completar el 100 %. */
export const PERFIL_NAV_SHOW_ALIANZA = 'showAlianzaLanding';

export function resolveNextSectionId(
  from: PerfilSeccionId,
  opts: { juridica?: boolean } = {}
): PerfilSeccionId | null {
  return (opts.juridica ? PERFIL_NEXT_SECTION_PJ : PERFIL_NEXT_SECTION)[from];
}

export function resolveNextSectionRoute(
  from: PerfilSeccionId,
  opts: { juridica?: boolean } = {}
): string | null {
  const nextId = resolveNextSectionId(from, opts);
  if (!nextId) {
    return null;
  }
  return PERFIL_SECTION_ROUTES[nextId];
}

export type PerfilSectionCtaKind = 'continuar' | 'guardar';

/** Último paso / sección de un solo paso: 100 % sin editar → Continuar. Si hay cambios → Guardar. */
export function resolvePerfilSectionCtaKind(
  sectionPercent: number,
  dirty: boolean
): PerfilSectionCtaKind {
  return sectionPercent >= 100 && !dirty ? 'continuar' : 'guardar';
}

/**
 * Multi-paso: intermedios siempre "Continuar"; último usa resolvePerfilSectionCtaKind.
 * La decisión de llamar al back es aparte: solo si hay cambios (dirty).
 */
export function resolvePerfilStepCtaKind(
  isLastStep: boolean,
  sectionPercent: number,
  dirty: boolean
): PerfilSectionCtaKind {
  if (!isLastStep) {
    return 'continuar';
  }
  return resolvePerfilSectionCtaKind(sectionPercent, dirty);
}

/**
 * Lee el state de la navegación actual.
 * Preferir en el constructor con getCurrentNavigation (patrón del repo);
 * en ngOnInit usar lastSuccessfulNavigation / history.state como fallback.
 */
export function readPerfilNavState(router: Router): Record<string, unknown> {
  const fromNav =
    router.getCurrentNavigation()?.extras?.state ??
    router.lastSuccessfulNavigation()?.extras?.state;
  if (fromNav && typeof fromNav === 'object') {
    return fromNav as Record<string, unknown>;
  }
  return (history.state ?? {}) as Record<string, unknown>;
}

export function consumeShowIntroFlag(state: Record<string, unknown>): void {
  if (!state[PERFIL_NAV_SHOW_INTRO]) {
    return;
  }
  try {
    history.replaceState({ ...state, [PERFIL_NAV_SHOW_INTRO]: false }, '');
  } catch {
    // ignore
  }
}

export function consumeShowEnviarFlag(state: Record<string, unknown>): void {
  if (!state[PERFIL_NAV_SHOW_ENVIAR]) {
    return;
  }
  try {
    history.replaceState({ ...state, [PERFIL_NAV_SHOW_ENVIAR]: false }, '');
  } catch {
    // ignore
  }
}

export function consumeShowAlianzaFlag(state: Record<string, unknown>): void {
  if (!state[PERFIL_NAV_SHOW_ALIANZA]) {
    return;
  }
  try {
    history.replaceState({ ...state, [PERFIL_NAV_SHOW_ALIANZA]: false }, '');
  } catch {
    // ignore
  }
}

/**
 * Tras Guardar / Continuar: va a la siguiente sección.
 * La sección decide si muestra el intro según solicitud/sección, no según porcentaje.
 * Si no hay siguiente (perfil completo), abre el hub (landing o modal enviar).
 */
export function navigateAfterSectionSave(
  router: Router,
  from: PerfilSeccionId,
  _nextSectionPercent = 0,
  opts: { juridica?: boolean } = {}
): void {
  const nextId = resolveNextSectionId(from, opts);
  if (!nextId) {
    void router.navigateByUrl(PERFIL_HUB_ROUTE, {
      state: {
        [PERFIL_NAV_SHOW_ENVIAR]: true,
        [PERFIL_NAV_SHOW_ALIANZA]: true,
      },
    });
    return;
  }
  const route = PERFIL_SECTION_ROUTES[nextId];
  if (route) {
    void router.navigateByUrl(route, {
      state: { [PERFIL_NAV_SHOW_INTRO]: true },
    });
    return;
  }
  void router.navigateByUrl(
    PERFIL_HUB_ROUTE,
    {
      state: {
        [PERFIL_NAV_SHOW_INTRO]: true,
        [PERFIL_NAV_INTRO_SECCION]: nextId,
      },
    }
  );
}

/** Keys con valor pero form.invalid (no suman al %; p. ej. fecha PAR). */
export function invalidFilledControlKeys(
  form: { controls: Record<string, { invalid: boolean }> }
): string[] {
  return Object.keys(form.controls).filter((key) => form.controls[key].invalid);
}

/** Tras hidratar: muestra error en rojo sin exigir clic en Continuar. */
export function markInvalidControlsTouched(form: {
  controls: Record<string, { invalid: boolean; markAsTouched: () => void }>;
}): void {
  for (const key of Object.keys(form.controls)) {
    const ctrl = form.controls[key];
    if (ctrl.invalid) {
      ctrl.markAsTouched();
    }
  }
}
